package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersAction
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.ScrollEvent
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.ui.components.toolbar.autohide.ReportActiveScroll
import com.eladfinish.bible.contest.ui.models.toComposeTextAlign
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlin.math.roundToInt

@Composable
internal fun ChapterLoadingView() {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        CircularProgressIndicator()
    }
}

@Composable
internal fun ChapterErrorView(message: String) {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Text("Failed to load chapter - $message")
    }
}

@Composable
internal fun ChapterTextView(
    chapterData: ChapterData,
    goToMarkerController: GoToMarkerController,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    onAction: (BrowseChaptersAction) -> Unit,
    textSize: Float,
    isActivePage: Boolean,
    stamFont: String?,
    contentMode: ContentMode,
    scrollEvent: ScrollEvent,
) {
    val scrollState = rememberScrollState()

    ReportActiveScroll(isActivePage, scrollState)

    val spannableModel: SpannableStringKMP? = when (contentMode) {
        is ContentMode.ChapterComplete -> contentMode.output
        is ContentMode.SavedCompletion -> contentMode.output
        is ContentMode.UserCompletions -> contentMode.output
        is ContentMode.ProgressiveExposure -> contentMode.output
        ContentMode.Normal -> null
        is ContentMode.ShuffledVerses -> null // should not reach here
    }

    val isSpannableMode = spannableModel != null

    if (!isSpannableMode) {
        LaunchedEffect(isActivePage, scrollState) {
            if (isActivePage) {
                goToMarkerController.onActiveScrollStateAvailable(scrollState)
            }
        }

        BindGoToMarkerController(
            isActivePage = isActivePage,
            scrollState = scrollState,
            rawText = chapterData.content,
            controller = goToMarkerController,
        )

        val textPaddingPx = with(LocalDensity.current) { ChapterTextPadding.toPx() }
        goToMarkerController.textPaddingPx = textPaddingPx
        LaunchedEffect(isActivePage, scrollState) {
            if (!isActivePage) return@LaunchedEffect
            snapshotFlow { scrollState.value }
                .distinctUntilChanged()
                .collect { y ->
                    val layout = goToMarkerController.activeLayout
                    val textOffset = if (layout == null || layout.layoutInput.text.isEmpty()) {
                        -1
                    } else {
                        layout.getLineStart(layout.getLineForVerticalPosition((y - textPaddingPx).coerceAtLeast(0f)))
                    }
                    onBookmarkScrollYChanged(y, textOffset)
                }
        }

        // The saved scroll position is in pixels, and at a new width the text wraps differently:
        // after a rotation the reader goes back to the line holding the same words.
        var anchorOffset by rememberSaveable { mutableIntStateOf(0) }
        var anchorWidth by rememberSaveable { mutableIntStateOf(0) }
        LaunchedEffect(isActivePage, scrollState) {
            if (!isActivePage) return@LaunchedEffect
            snapshotFlow { goToMarkerController.activeLayout to scrollState.value }
                .collect { (layout, y) ->
                    if (layout == null || layout.layoutInput.text.isEmpty()) return@collect
                    val width = layout.size.width
                    if (anchorWidth != 0 && anchorWidth != width) {
                        anchorWidth = width
                        val line = layout.getLineForOffset(anchorOffset.coerceIn(0, layout.layoutInput.text.length))
                        scrollState.scrollTo((layout.getLineTop(line) + textPaddingPx).roundToInt())
                    } else {
                        anchorWidth = width
                        anchorOffset = layout.getLineStart(layout.getLineForVerticalPosition((y - textPaddingPx).coerceAtLeast(0f)))
                    }
                }
        }
    }

    LaunchedEffect(scrollEvent, isActivePage, isSpannableMode) {
        if (!isActivePage) return@LaunchedEffect

        when (scrollEvent) {
            ScrollEvent.None -> Unit

            ScrollEvent.ToTop -> {
                kotlinx.coroutines.yield()
                scrollState.animateScrollTo(0)
                onAction(BrowseChaptersAction.ConsumeScrollEvent)
            }

            ScrollEvent.ToBottom -> {
                if (!isSpannableMode) return@LaunchedEffect
                kotlinx.coroutines.yield()
                scrollState.animateScrollTo(scrollState.maxValue)
                onAction(BrowseChaptersAction.ConsumeScrollEvent)
            }
        }
    }

    if (isSpannableMode) {
        ChapterCompleteText(
            model = spannableModel,
            scrollState = scrollState,
            textAlign = chapterData.textAlign?.toComposeTextAlign(),
            textSize = textSize,
            onActionClick = { clickAction ->
                onAction(
                    BrowseChaptersAction.ChapterCompleteClick(
                        clickAction = clickAction,
                    ),
                )
            },
        )
    } else {
        val annotated = rememberChapterAnnotatedText(
            chapterData = chapterData,
            controller = goToMarkerController,
            baseFontSize = textSize.sp,
        )

        ChapterText(
            annotated = annotated,
            scrollState = scrollState,
            textAlign = chapterData.textAlign?.toComposeTextAlign(),
            textSize = textSize,
            stamFont = stamFont,
            onTextLayout = { layout ->
                goToMarkerController.layoutResult = layout
                if (isActivePage) goToMarkerController.onActivePageLaidOut(layout, scrollState)
            },
            onCopySelected = { selected ->
                onAction(
                    BrowseChaptersAction.CopySelectedText(
                        rawSelectedText = selected,
                    ),
                )
            },
            onSearchSelected = { query ->
                onAction(
                    BrowseChaptersAction.SearchSelectedText(
                        rawSelectedText = query,
                    ),
                )
            },
            useCustomSelection = false,
        )
    }
}
